import { removeDupes, cleanObject } from '../utils/helpers';

const getGroupKey = payload => {
    if (payload.parentDocId) {
        return 'pages';
    }

    if (payload.folderId) {
        return 'folder';
    }

    if (payload.projectId) {
        return 'project';
    }

    if (payload.userId) {
        return 'user';
    }

    return null;
}

const getIDFromPayload = payload => payload.parentDocId || payload.folderId || payload.projectId || payload.userId;

const docs = (state = {
    items: [],
    fetchInfo: {
        user: {},
        project: {},
        folder: {},
        pages: {}
    }
}, action) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
            return {
                items: [],
                fetchInfo: {
                    user: {},
                    project: {},
                    folder: {},
                    pages: {}
                }
            };

        case 'MARK_ALL_FOR_REFETCH':
            return Object.assign({}, state, {
                items: [...state.items.map(doc => ({
                    ...doc,
                    shouldRefetch: true
                }))]
            });

        case 'DOCS_FETCH_REQUESTED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            if (!key) {
                return state;
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            ...state.fetchInfo[key][id],
                            loading: true
                        }
                    }
                }
            });
        }

        case 'DOCS_FETCH_SUCCEEDED': {
            if (!action.payload.docs) {
                return state;
            }

            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);
            const items = removeDupes([...action.payload.docs, ...state.items]);

            return Object.assign({}, state, {
                items,
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            fetched: true,
                            loading: false,
                            hasMore: action.payload.hasMore
                        }
                    }
                }
            });
        }

        case 'DOCS_FETCH_FAILED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            fetched: true,
                            loading: false,
                            error: true
                        }
                    }
                }
            });
        }

        case 'DOC_CREATE_REQUESTED':
        case 'DOC_ADD_SOCKET':
            // Don't modify if exists
            if (action.payload.doc.id && state.items.find(doc => doc.id === action.payload.doc.id)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [action.payload.doc, ...state.items]
            });

        case 'DOC_CREATE_SUCCEEDED':
            return Object.assign({}, state, {
                items: state.items
                    .filter(doc => doc.id !== action.payload.docId)
                    .map(doc => {
                        if (doc === action.payload.fragment) {
                            return Object.assign({}, doc, {
                                id: action.payload.docId
                            });
                        }

                        return doc;
                    })
            });

        case 'DOC_CREATE_FAILED':
            return Object.assign({}, state, {
                items: state.items.filter(doc => doc !== action.payload.fragment)
            });

        case 'DOC_FETCH_REQUESTED':
            if (action.payload.fromToken) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.filter(doc => doc.id !== action.payload.docId), {
                    ...(state.items.find(doc => doc.id === action.payload.docId) || {}),
                    id: action.payload.docId,
                    project_id: action.payload.projectId,
                    loading: true
                }]
            });

        case 'DOC_FETCH_FAILED':
            return Object.assign({}, state, {
                items: [...state.items.filter(doc => doc.id !== action.payload.docId), {
                    id: action.payload.docId,
                    error: true
                }]
            });

        case 'DOC_FETCH_SUCCEEDED':
            if (!action.payload || !action.payload.doc) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.filter(doc => doc.id !== action.payload.doc.id && doc.id !== action.payload.doc.public_token), {
                    ...action.payload.doc, 
                    project_id: action.payload.projectId || action.payload.doc.project?.id,
                    comments: ((action.payload.doc.comments || {}).items || []).length
                }]
            });

        case 'DOC_UPDATE_REQUESTED':
        case 'DOC_UPDATE_SOCKET':
        case 'DOC_MOVE_REQUESTED':
            if (!state.items.some(doc => doc.id === action.payload.docId)) {
                return Object.assign({}, state, {
                    items: [...state.items, (action.payload.doc || action.payload.moveProps)]
                });
            }

            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, cleanObject(action.payload.docProps || action.payload.doc || action.payload.moveProps));
                    }

                    return doc;
                })
            });

        case 'DOC_ARCHIVE_REQUESTED':
        case 'DOC_ARCHIVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return {
                            ...doc,
                            archived: true
                        };
                    }

                    return doc;
                })
            });

        case 'DOC_UNARCHIVE_REQUESTED':
        case 'DOC_UNARCHIVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return {
                            ...doc,
                            archived: false
                        };
                    }

                    return doc;
                })
            });

        case 'DOC_REMOVE_REQUESTED':
        case 'DOC_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return {
                            id: doc.id,
                            removed: true
                        };
                    }

                    return doc;
                })
            });

        case 'DOC_ADD_MEMBERS_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            members: removeDupes([...doc.members, ...action.payload.members])
                                .map(user => {
                                    const updatedUser = action.payload.members.find(u => u.id === user.id);

                                    if (updatedUser) {
                                        return {
                                            ...user,
                                            permission: updatedUser.permission
                                        }
                                    }

                                    return user;
                                })
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_USER_EDIT_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            members: [...doc.members]
                                .map(user => {
                                    if (user.id === action.payload.userId) {
                                        return {
                                            ...user,
                                            last_edited: action.payload.last_edited
                                        }
                                    }

                                    return user;
                                })
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_REMOVE_MEMBERS_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            members: doc.members.filter(user => !user || !action.payload.members.includes(user.id))
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_LABELS_ADD_REQUESTED':
            if (!state.items.some(doc => doc.id === action.payload.docId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            labels: [...new Set([...(doc.labels || []), ...action.payload.labels])]
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_LABELS_REMOVE_REQUESTED':
            if (!state.items.some(doc => doc.id === action.payload.docId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            labels: (doc.labels || []).filter(labelId => !action.payload.labels.includes(labelId))
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_GET_SHARE_LINK_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            shareLinkLoading: true
                        });
                    }

                    return doc;
                })
            });

        case 'DOC_GET_SHARE_LINK_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.docId) {
                        return Object.assign({}, doc, {
                            shareLink: action.payload.shareLink,
                            shareLinkLoading: false
                        });
                    }

                    return doc;
                })
            });

        case 'MESSAGE_ADD_SOCKET':
        case 'MESSAGE_REMOVE_SOCKET':
            if (!action.payload.message || action.payload.message.subtype || !state.items.some(doc => doc.id === action.payload.message.document)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(doc => {
                    if (doc.id === action.payload.message.document) {
                        return Object.assign({}, doc, {
                            comments: action.type === 'MESSAGE_ADD_SOCKET' ? (doc.comments || 0) + 1 : Math.max(0, (doc.comments || 0) - 1)
                        });
                    }

                    return doc;
                })
            });

        case 'USER_INVITE_CANCEL_SOCKET':
        case 'USER_REMOVE_REQUESTED':
        case 'PROJECT_USER_REMOVE_SOCKET':
        case 'TEAM_USER_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items.map(doc => {
                    if (action.payload.projectId && doc.project !== action.payload.projectId) {
                        return doc;
                    }

                    if (doc.members && doc.members.length && doc.members.some(({ id }) => id === action.payload.userId)) {
                        return Object.assign({}, doc, {
                            members: doc.members.filter(({ id }) => id !== action.payload.userId)
                        });
                    }

                    return doc;
                })]
            });

        case 'PROJECT_REMOVE_REQUESTED':
        case 'PROJECT_REMOVE_SOCKET':
            if (!state.items.some(doc => doc.project === action.payload.projectId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: state.items.filter(doc => !doc.project || doc.project !== action.payload.projectId)
            });

        default:
            return state;
    }
}

export default docs;
